import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import api from "../lib/api";
import "../public-inquiry.css";

const roles = ["Spieler", "Berater", "Verein", "Sonstiges"];
const initialValues = { name: "", role: "", email: "", phone: "", organization: "", message: "", website: "" };
const failureMessage = "Das hat gerade nicht funktioniert. Bitte versuche es erneut.";
const limits = { name: 100, email: 254, phone: 50, organization: 160, message: 2000, website: 200 };

export function validateInquiry(values) {
  const errors = {};
  if (!values.name.trim()) errors.name = "Bitte gib deinen Namen ein.";
  if (!roles.includes(values.role)) errors.role = "Bitte wähle aus, wer du bist.";
  if (!/^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/.test(values.email.trim())) errors.email = "Bitte gib eine gültige E-Mail-Adresse ein.";
  for (const [field, limit] of Object.entries(limits)) {
    if (values[field].length > limit) errors[field] = `Bitte verwende höchstens ${limit} Zeichen.`;
  }
  return errors;
}

export default function PublicInquiry() {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const inFlight = useRef(false);
  const submission = useRef(null);
  const form = useRef(null);
  const success = useRef(null);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Performance Care anfragen";
    window.scrollTo(0, 0);
    return () => { document.title = previousTitle; };
  }, []);

  useEffect(() => {
    if (status === "success") success.current?.focus();
  }, [status]);

  const change = (event) => {
    const { name, value } = event.target;
    setValues(current => ({ ...current, [name]: value }));
    setErrors(current => ({ ...current, [name]: undefined }));
    if (status === "error") setStatus("idle");
  };

  const submit = async (event) => {
    event.preventDefault();
    if (inFlight.current) return;
    const nextErrors = validateInquiry(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      form.current.elements.namedItem(Object.keys(nextErrors)[0])?.focus?.();
      if (Object.keys(nextErrors)[0] === "role") form.current.querySelector('[name="role"]')?.focus();
      return;
    }
    inFlight.current = true;
    setStatus("submitting");
    try {
      const payload = Object.fromEntries(Object.entries(values).map(([key, value]) => [key, value.trim()]));
      const fingerprint = JSON.stringify(payload);
      // Retain the key when retrying an uncertain response; edits start a new request.
      if (submission.current?.fingerprint !== fingerprint) {
        const key = Array.from(window.crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, "0")).join("");
        submission.current = { fingerprint, key };
      }
      const { data } = await api.post("/public/inquiries", { ...payload, submission_key: submission.current.key });
      if (data?.status !== "received") throw new Error("Unconfirmed inquiry");
      setStatus("success");
      setValues(initialValues);
    } catch {
      setStatus("error");
    } finally {
      inFlight.current = false;
    }
  };

  const field = (name, label, type = "text", autoComplete) => <div className="pc-inquiry__field">
    <label htmlFor={`inquiry-${name}`}>{label}{!["name", "email"].includes(name) && <span>optional</span>}</label>
    <input id={`inquiry-${name}`} name={name} type={type} autoComplete={autoComplete}
      value={values[name]} onChange={change} maxLength={limits[name]} required={["name", "email"].includes(name)}
      aria-invalid={Boolean(errors[name])} aria-describedby={errors[name] ? `inquiry-${name}-error` : undefined} />
    {errors[name] && <p className="pc-inquiry__field-error" id={`inquiry-${name}-error`}>{errors[name]}</p>}
  </div>;

  return <div className="pc-inquiry" lang="de">
    <a className="pc-inquiry__skip" href="#inquiry-main">Zum Inhalt</a>
    <header className="pc-inquiry__header pc-inquiry__shell">
      <Link to="/" className="pc-inquiry__brand" aria-label="Performance Care Startseite">PERFORMANCE<span>CARE.</span></Link>
      <Link className="pc-inquiry__back" to="/"><ArrowLeft size={15} aria-hidden="true" />Zur Startseite</Link>
    </header>
    <main className={`pc-inquiry__main pc-inquiry__shell${status === "success" ? " pc-inquiry__main--success" : ""}`} id="inquiry-main" tabIndex={-1}>
      {status !== "success" && <div className="pc-inquiry__intro">
        <div className="pc-inquiry__accent" aria-hidden="true" />
        <h1>Performance Care<br />anfragen.</h1>
        <p>Erzähl uns kurz, wer du bist. Wir melden uns persönlich bei dir.</p>
      </div>}
      {status === "success" ? <section className="pc-inquiry__success" aria-labelledby="inquiry-success">
        <div className="pc-inquiry__accent" aria-hidden="true" />
        <h1 id="inquiry-success" ref={success} tabIndex={-1}>Anfrage gesendet.</h1>
        <p>Wir melden uns persönlich bei dir.</p>
        <Link className="pc-inquiry__button" to="/">Zur Startseite <ArrowUpRight size={18} aria-hidden="true" /></Link>
      </section> : <form ref={form} className="pc-inquiry__form" onSubmit={submit} noValidate aria-label="Performance Care Anfrage" aria-busy={status === "submitting"}>
        <fieldset className="pc-inquiry__fields" disabled={status === "submitting"}>
          {field("name", "Name", "text", "name")}
          <fieldset className="pc-inquiry__role" aria-describedby={errors.role ? "inquiry-role-error" : undefined} aria-invalid={Boolean(errors.role)}>
            <legend>Ich bin</legend>
            <div className="pc-inquiry__choices">{roles.map(role => <label key={role}>
              <input type="radio" name="role" value={role} checked={values.role === role} onChange={change} required />
              <span>{role}</span>
            </label>)}</div>
            {errors.role && <p className="pc-inquiry__field-error" id="inquiry-role-error">{errors.role}</p>}
          </fieldset>
          <div className="pc-inquiry__row">
            {field("email", "E-Mail", "email", "email")}
            {field("phone", "Telefon", "tel", "tel")}
          </div>
          {field("organization", "Verein / Organisation", "text", "organization")}
          <div className="pc-inquiry__field">
            <label htmlFor="inquiry-message">Worum geht es?<span>optional</span></label>
            <textarea id="inquiry-message" name="message" rows={3} maxLength={limits.message} value={values.message} onChange={change}
              placeholder="Was können wir für dich übernehmen?" aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? "inquiry-message-error" : undefined} />
            {errors.message && <p className="pc-inquiry__field-error" id="inquiry-message-error">{errors.message}</p>}
          </div>
          <div className="pc-inquiry__trap" aria-hidden="true">
            <label htmlFor="inquiry-website">Website</label>
            <input id="inquiry-website" name="website" value={values.website} onChange={change} maxLength={200} tabIndex={-1} autoComplete="off" />
          </div>
        </fieldset>
        {status === "error" && <p className="pc-inquiry__error" role="alert">{failureMessage}</p>}
        <button className="pc-inquiry__button" type="submit" disabled={status === "submitting"}>
          {status === "submitting" ? "Wird gesendet …" : "Anfrage senden"}<ArrowUpRight size={18} aria-hidden="true" />
        </button>
        <p className="pc-inquiry__privacy">Hinweise zum Umgang mit deinen Daten findest du in der <Link to="/datenschutz">Datenschutzerklärung</Link>.</p>
      </form>}
    </main>
  </div>;
}
